import React, { useContext, useState, useEffect, useRef } from 'react';
import { WebRTCContext } from './contexts/WebRTCContext';
import { useMIDI } from './contexts/MIDIContext';

function MIDISequencer() {
  // Sequencer state
  const [isPlaying, setIsPlaying] = useState(false);
  const [tempo, setTempo] = useState(120); // BPM
  const [stepNotes, setStepNotes] = useState(Array(16).fill("C4"));
  const [noteDurationFactor, setNoteDurationFactor] = useState(1); // Duration factor relative to a quarter note
  const [isLocalMIDIEnabled, setIsLocalMIDIEnabled] = useState(false);

  // Get MIDI state and functions from the MIDIContext
  const {
    midiAccess,
    selectedInput,
    selectedOutput,
    sendMIDI,
  } = useMIDI();

  const { sendData } = useContext(WebRTCContext);

  // Duration options for the dropdown
  const durationOptions = [
    { label: "1/16", factor: 0.25 },
    { label: "1/8", factor: 0.5 },
    { label: "1/4", factor: 1 },
    { label: "1/2", factor: 2 },
    { label: "1", factor: 4 },
  ];

  // Refs to track the current step and the interval timer
  const stepIndexRef = useRef(0);
  const sequencerIntervalRef = useRef(null);

  // Generate an array of note names (including sharps)
  const noteNames = [];
  const allNotes = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
  for (let octave = 0; octave <= 10; octave++) {
    allNotes.forEach((note) => {
      noteNames.push(`${note}${octave}`);
    });
  }

  // Calculate quarter note duration (in ms) based on tempo
  const getBeatDuration = () => 60000 / tempo;

  // Mapping function: convert note name (e.g., "C4") to MIDI note number.
  const noteNameToMidi = (noteName) => {
    const match = noteName.match(/^([A-G]#?)(\d+)$/);
    if (!match) return null;
    const noteStr = match[1];
    const octave = parseInt(match[2], 10);
    const noteOffsets = { 
      "C": 0, "C#": 1, "D": 2, "D#": 3, "E": 4, 
      "F": 5, "F#": 6, "G": 7, "G#": 8, "A": 9, "A#": 10, "B": 11 
    };
    return 12 + octave * 12 + noteOffsets[noteStr];
  };

  // Schedule the next note based on the current step
  const scheduleNextNote = () => {
    if (!selectedOutput) return;
    const baseDuration = getBeatDuration(); // quarter note duration
    const noteDuration = baseDuration * noteDurationFactor;
    const currentNote = stepNotes[stepIndexRef.current];
    const midiNote = noteNameToMidi(currentNote);
    if (midiNote !== null) {
      // Calculate the correct MIDI command using the selected output channel (0-indexed)
      const noteOnCommand = 0x90;
      const noteOffCommand = 0x80;
      const midiMessage = [noteOnCommand, midiNote, 100];
      isLocalMIDIEnabled && sendMIDI(noteOnCommand, midiNote, 100);
      sendData(midiMessage);
      console.log("Sent MIDI Note On:", midiMessage);
      // Send Note Off after 90% of the note duration
      setTimeout(() => {
        const noteOffMessage = [noteOffCommand, midiNote, 0];
        isLocalMIDIEnabled && sendMIDI(noteOffCommand, midiNote, 0);
        sendData(noteOffMessage);
        console.log("Sent MIDI Note Off:", noteOffMessage);
      }, noteDuration * 0.9);
    }
    // Advance to the next step (wrap around after step 15)
    stepIndexRef.current = (stepIndexRef.current + 1) % 16;
  };

  // Start the sequencer: schedule a note every (quarter note duration * noteDurationFactor)
  const startSequencer = () => {
    if (!selectedOutput) {
      alert("No MIDI output available.");
      return;
    }
    if (isPlaying) return;
    setIsPlaying(true);
    stepIndexRef.current = 0; // reset sequence to beginning
    scheduleNextNote();       // schedule the first note immediately
    sequencerIntervalRef.current = setInterval(scheduleNextNote, getBeatDuration() * noteDurationFactor);
  };

  // Stop the sequencer
  const stopSequencer = () => {
    setIsPlaying(false);
    if (sequencerIntervalRef.current) {
      clearInterval(sequencerIntervalRef.current);
      sequencerIntervalRef.current = null;
    }
  };

  // Update the interval if the tempo, noteDurationFactor, or playing status changes
  useEffect(() => {
    if (isPlaying) {
      if (sequencerIntervalRef.current) {
        clearInterval(sequencerIntervalRef.current);
      }
      sequencerIntervalRef.current = setInterval(scheduleNextNote, getBeatDuration() * noteDurationFactor);
    }
    return () => {
      if (sequencerIntervalRef.current) {
        clearInterval(sequencerIntervalRef.current);
      }
    };
  }, [tempo, noteDurationFactor, isPlaying]);

  // Handle a change in one of the step dropdowns
  const handleStepChange = (index, newNote) => {
    const newSteps = [...stepNotes];
    newSteps[index] = newNote;
    setStepNotes(newSteps);
  };

  // Create an array for tempo dropdown options (30 BPM to 200 BPM)
  const tempoOptions = [];
  for (let t = 30; t <= 200; t++) {
    tempoOptions.push(t);
  }

  return (
    <div style={{ padding: '1rem', border: '1px solid #ccc', margin: '1rem', backgroundColor: '#c3d9e4' }}>
      <h2>MIDI 16-Step Sequencer</h2>
      {/* Only show the sequencer UI after MIDI is enabled */}
      {!midiAccess ? (
        <p>No MIDI detected. Please enable MIDI.</p>
      ) : (
        <>
          {selectedInput ? (
            <div>
              <p>Using MIDI input: {selectedInput.name}</p>
            </div>
          ) : (
            <p>No MIDI output detected.</p>
          )}

          {/* 16-step row */}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
            {stepNotes.map((note, index) => (
              <div key={index} style={{ margin: '0 0.25rem' }}>
                <select
                  value={note}
                  onChange={(e) => handleStepChange(index, e.target.value)}
                >
                  {noteNames.map((n, i) => (
                    <option key={i} value={n}>{n}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>

          {/* Controls: Start, Stop, Tempo Dropdown, Duration Dropdown */}
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem' }}>
            <button onClick={startSequencer} disabled={isPlaying}>Start</button>
            <button onClick={stopSequencer} disabled={!isPlaying}>Stop</button>
            <div>
              <label>
                Tempo:
                <select value={tempo} onChange={(e) => setTempo(Number(e.target.value))} style={{ marginLeft: '0.5rem' }}>
                  {tempoOptions.map((t, i) => (
                    <option key={i} value={t}>{t} BPM</option>
                  ))}
                </select>
              </label>
            </div>
            <div>
              <label>
                Duration:
                <select 
                  value={noteDurationFactor} 
                  onChange={(e) => setNoteDurationFactor(Number(e.target.value))}
                  style={{ marginLeft: '0.5rem' }}
                >
                  {durationOptions.map((option, i) => (
                    <option key={i} value={option.factor}>{option.label}</option>
                  ))}
                </select>
              </label>
            </div>
              <div>
                <label>
                  <input
                    type="checkbox"
                    checked={isLocalMIDIEnabled}
                    onChange={(e) => setIsLocalMIDIEnabled(e.target.checked)}
                  />
                  Local MIDI Enabled
                </label>
              </div>
            </div>
        </>
      )}
    </div>
  );
}

export default MIDISequencer;